import { ColumnDef } from '@tanstack/react-table'
import {
  Box,
  Flex,
  Link,
  Loading,
  Table,
  Text,
  useAppTheme,
} from '@workwhile/ui'
import { UserPayment } from 'api/payments'
import { AccordionCard } from 'components/AccordionCard'
import { InfoTable } from 'components/InfoTable'
import { formatInTimeZone } from 'date-fns-tz'
import { useGlobalWorkflowsContextValue } from 'global-workflows/GlobalWorkflowManager/GlobalWorkflowManager'
import { formatCurrency } from 'lib/currency'
import { roundNumber } from 'lib/number'
import { getLongDuration } from 'lib/time'
import { Ban, Clock, Database, HardHat, MapPin, Pencil } from 'lucide-react'
import { TimelineTripModal } from 'pages/review/components/TimelineTripModal/TimelineTripModal'
import {
  AmountColumn,
  DescriptionColumn,
  ItemsColumn,
  PaymentIDColumn,
  ProviderColumn,
  StatusColumn,
} from 'pages/UserPayments/components/ColumnsFilter/columns'
import {
  UserPaymentsProvider,
  useUserPaymentsContextValue,
} from 'pages/UserPayments/context/UserPaymentsProvider'
import { useShiftAssessments } from 'queries/user'
import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { useShiftDetailContextValue } from '../../context/ShiftDetailProvider'
import { Work } from '../../types'
import { ActionIcon, AssessmentWorkflowIcon } from './ActionIcon'
import { leftTdProps, rightTdProps } from './constants'
import { ReadOnlyBox } from './ReadOnlyBox'
import { StatusTransitions } from './StatusTransitions'
import { WorkerRowBreaksTable } from './WorkerRowBreaksTable'
import { WorkerShiftStatus } from './WorkerShiftStatus'
import { WorkerStatus } from './WorkerStatus'
import { DayforceWorkerStatus } from './Dayforce/DayforceWorkerStatus'
import { DayforceActions } from './Dayforce/DayforceActions'
import { JetWorkerStatus } from './Jet/JetWorkerStatus'
import { JetTimesheetPhoto } from './Jet/JetTimesheetPhoto'

type WorkerRowProps = {
  work: Work
} & (
  | {
      allowEdit: true
    }
  | {
      allowEdit?: never
    }
)

const StyledInfoTable = styled(InfoTable)`
  td {
    vertical-align: text-top;
  }
`
// Custom columns for WorkerRow: Payment ID, Amount, Status, Description, Provider, Items (for OAI breakdown)
const paymentColumns: ColumnDef<UserPayment>[] = [
  PaymentIDColumn,
  AmountColumn,
  StatusColumn,
  DescriptionColumn,
  ProviderColumn,
  ItemsColumn,
] as const

const WorkerRowPaymentsTable = ({
  columns,
}: {
  columns: ColumnDef<UserPayment>[]
}) => {
  const {
    state: { data, isFetching },
  } = useUserPaymentsContextValue()

  const doesHaveData = data.items.length > 0

  return (
    <Box>
      {doesHaveData ? (
        <Table columns={columns} data={data.items} textVerticalAlign="top" />
      ) : (
        <Flex
          mb={2}
          flexDirection="column"
          alignItems="center"
          color="lightText"
          height={100}
          justifyContent="center"
          maxWidth={['100%', '100%', '50%', '50%']}
          margin="auto"
        >
          {isFetching ? <Loading /> : <Text>No payments found.</Text>}
        </Flex>
      )}
    </Box>
  )
}

export const WorkerRow = ({ work, allowEdit }: WorkerRowProps) => {
  const {
    id: workId,
    worker: { penaltyCount, totalShiftsCount, id: workerId, name },
    startedAt,
    completedAt,
    status,
    earnings,
    payBreakdown,
    statusTransitions,
    removedReason,
    leftEarly,
    confirmedAt,
    commuteDetails,
    trips,
    jetCallReport,
  } = work
  const { space } = useAppTheme()
  const { editShiftWorkFlow } = useGlobalWorkflowsContextValue()
  const { hash } = useLocation()
  const workAnchorId = `work-${workId}`
  const isHashTarget = hash === `#${workAnchorId}`

  /** Work context */
  const [showTimelineTripModal, setShowTimelineTripModal] = useState(false)

  useEffect(() => {
    if (!isHashTarget) {
      return
    }
    // Delay so async content above Workers (listings, stats) can finish laying out.
    const timeoutId = window.setTimeout(() => {
      document
        .getElementById(workAnchorId)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 100)
    return () => window.clearTimeout(timeoutId)
  }, [isHashTarget, workAnchorId])

  /** Shift context */
  const {
    state: {
      location,
      startsAt: shiftStartsAt,
      endsAt: shiftEndsAt,
      id: shiftId,
      payRate,
      payLumpSum,
      shiftBonuses,
      lunchBreak,
      requiresJetClockOutCallReport,
    },
  } = useShiftDetailContextValue()

  const {
    address: { timezone },
  } = location

  /** Assessment context */
  const { data: assessment } = useShiftAssessments(shiftId)

  const bonusTotal =
    shiftBonuses?.reduce((acc, bonus) => acc + bonus.amount, 0) ?? 0

  return (
    <>
      <AccordionCard
        defaultOpen={isHashTarget}
        title={
          <>
            <Flex
              alignItems={['flex-start', 'flex-start', 'flex-start', 'center']}
              width="100%"
              gap={[2, 2, 2, 4]}
              flexDirection={['column', 'column', 'column', 'row']}
            >
              <Flex
                flexDirection="column"
                gap={1}
                minWidth="400px"
                maxWidth="400px"
              >
                <Text
                  data-context-key="worker-id"
                  data-context-value={workerId}
                  data-context-label="Worker ID"
                >
                  {name}{' '}
                  <Link to={`/users/${workerId}`} target="_blank">
                    ({workerId})
                  </Link>
                </Text>
                <WorkerShiftStatus
                  workerId={workerId}
                  status={status}
                  startedAt={startedAt}
                  completedAt={completedAt}
                  removedReason={removedReason}
                  timezone={timezone}
                  payBreakdown={payBreakdown}
                />
              </Flex>
              <WorkerStatus status={status} minWidth="auto" />
              {typeof earnings === 'number' && (
                <Text>
                  Est. {formatCurrency(earnings)}
                  {earnings > 0 && bonusTotal > 0
                    ? ` + ${formatCurrency(bonusTotal)} bonus`
                    : ''}
                </Text>
              )}

              <DayforceWorkerStatus work={work} />
              <JetWorkerStatus
                report={jetCallReport}
                requiresJetClockOutCallReport={requiresJetClockOutCallReport}
              />
            </Flex>
            <Flex alignItems="center" justifyContent="flex-center">
              {assessment.requirements.length > 0 ? (
                <AssessmentWorkflowIcon
                  worker={{ id: workerId, name }}
                  shiftId={shiftId}
                />
              ) : null}

              {allowEdit ? (
                <ActionIcon
                  icon={Pencil}
                  title="Edit Work"
                  onClick={() => {
                    editShiftWorkFlow.open({
                      shiftId,
                      workId,
                      worker: {
                        id: workerId,
                        name,
                      },
                      shiftStartsAt,
                      shiftEndsAt,
                      payRate,
                      payLumpSum,
                      shiftBonuses,
                      lunchBreak,
                      status,
                      clockedInAt: startedAt,
                      clockedOutAt: completedAt,
                      leftEarly,
                      timezone,
                    })
                  }}
                />
              ) : null}

              {trips?.length ? (
                <ActionIcon
                  icon={MapPin}
                  title="View Timeline & Trip"
                  onClick={() => setShowTimelineTripModal(true)}
                />
              ) : null}
            </Flex>
          </>
        }
        isAccordion
        contentWrapperProps={{
          id: workAnchorId,
          display: 'flex',
          flexDirection: 'column',
          gap: space[5],
        }}
      >
        {/* Please do not put actions here. We want to keep actions as "modals" and use the workflow pattern to achieve them. This is a view only component. */}
        <Flex
          flexDirection="row"
          gap={space[2]}
          data-context-key="work-id"
          data-context-value={workId}
          data-context-label="Work ID"
        >
          <StyledInfoTable
            style={{
              maxWidth: 'fit-content',
              tableLayout: 'auto',
              borderSpacing: `0 ${space[2]}`,
              display: 'inline-block',
            }}
          >
            <InfoTable.Row
              icon={<Database size={14} />}
              label="Work ID"
              value={<ReadOnlyBox>{workId}</ReadOnlyBox>}
              leftTdProps={leftTdProps}
              rightTdProps={rightTdProps}
            />
            <InfoTable.Row
              icon={<Ban size={14} />}
              label="Penalties"
              value={<ReadOnlyBox>{penaltyCount}</ReadOnlyBox>}
              leftTdProps={leftTdProps}
              rightTdProps={rightTdProps}
            />
            <InfoTable.Row
              icon={<HardHat size={14} />}
              label="Total Shifts"
              value={<ReadOnlyBox>{totalShiftsCount}</ReadOnlyBox>}
              leftTdProps={leftTdProps}
              rightTdProps={rightTdProps}
            />
            {commuteDetails ? (
              <>
                <InfoTable.Row
                  icon={<Clock size={14} />}
                  label="Commute Mode"
                  value={
                    <ReadOnlyBox style={{ textTransform: 'capitalize' }}>
                      {commuteDetails.mode.toLowerCase()}
                    </ReadOnlyBox>
                  }
                  leftTdProps={leftTdProps}
                  rightTdProps={rightTdProps}
                />
                <InfoTable.Row
                  icon={<Clock size={14} />}
                  label="Commute Duration"
                  value={
                    <ReadOnlyBox>
                      {getLongDuration(
                        roundNumber(commuteDetails.durationMinutes, 2)
                      )}
                    </ReadOnlyBox>
                  }
                  leftTdProps={leftTdProps}
                  rightTdProps={rightTdProps}
                />
              </>
            ) : null}
          </StyledInfoTable>

          <StyledInfoTable
            style={{
              maxWidth: 'fit-content',
              tableLayout: 'auto',
              borderSpacing: `0 ${space[2]}`,
              display: 'inline-block',
            }}
          >
            <InfoTable.Row
              icon={<Clock size={14} />}
              label="Confirmed at"
              value={
                <ReadOnlyBox>
                  {confirmedAt
                    ? formatInTimeZone(
                        confirmedAt,
                        timezone,
                        'EE, MMMM d, yyyy, h:mm a z'
                      )
                    : 'N/A'}
                </ReadOnlyBox>
              }
              leftTdProps={leftTdProps}
              rightTdProps={rightTdProps}
            />
            <StatusTransitions statusTransitions={statusTransitions} />
          </StyledInfoTable>
          <DayforceActions work={work} />
          <JetTimesheetPhoto
            work={work}
            report={jetCallReport}
            requiresJetClockOutCallReport={requiresJetClockOutCallReport}
          />
        </Flex>

        {/* Paid employer_cancelled works keep their status, so show payments for them too */}
        {status === 'paid' || status === 'employer_cancelled' ? (
          <Box>
            <UserPaymentsProvider
              initialUserId={workerId}
              initialShiftId={shiftId}
              initialSelectedColumns={['comment', 'provider']}
            >
              <WorkerRowPaymentsTable columns={paymentColumns} />
            </UserPaymentsProvider>
          </Box>
        ) : null}
        <WorkerRowBreaksTable
          shiftId={shiftId}
          workId={workId}
          timezone={timezone}
          clockedInAt={startedAt}
          clockedOutAt={completedAt}
          allowEdit={allowEdit}
        />
      </AccordionCard>
      {showTimelineTripModal ? (
        <TimelineTripModal
          open={showTimelineTripModal}
          onClose={() => setShowTimelineTripModal(false)}
          work={
            {
              id: workId,
              status,
              startedAt,
              completedAt,
              confirmedAt,
              reasonEarlyClockout: removedReason,
              trips: trips?.map((trip) => ({ embedUrl: trip.embedUrl })) || [],
              shift: {
                id: shiftId,
                startsAt: shiftStartsAt,
                location: {
                  address: location.address,
                },
              },
              worker: {
                id: String(workerId),
                name,
              },
            } as any
          }
        />
      ) : null}
    </>
  )
}
